export const createEmptyStructuredJsonSample = () => ({
    fileName: '',
    valid: false,
    error: '',
    json: '',
    paths: []
});

export const parseStructuredJsonSample = (fileName, rawJson) => {
    try {
        const parsed = JSON.parse(rawJson);
        const minified = JSON.stringify(parsed);
        return {
            fileName: fileName || '',
            valid: true,
            error: '',
            json: minified,
            paths: collectJsonPaths(parsed)
        };
    } catch (error) {
        return {
            fileName: fileName || '',
            valid: false,
            error: error && error.message ? error.message : 'Invalid JSON',
            json: '',
            paths: []
        };
    }
};

export const collectJsonPaths = (value, prefix = '$', result = []) => {
    if (value === null || value === undefined) {
        result.push(prefix);
        return result;
    }

    if (Array.isArray(value)) {
        if (value.length === 0) {
            result.push(prefix + '[*]');
            return result;
        }

        collectJsonPaths(value[0], prefix + '[*]', result);
        return result;
    }

    if (typeof value === 'object') {
        const keys = Object.keys(value);
        if (keys.length === 0) {
            result.push(prefix);
            return result;
        }

        keys.forEach(key => {
            collectJsonPaths(value[key], prefix + '.' + key, result);
        });
        return Array.from(new Set(result));
    }

    result.push(prefix);
    return result;
};

export const getStructuredJsonSample = (docParameters) => {
    const sample = docParameters && docParameters.StructuredJsonSample;
    if (!sample || !sample.valid || !sample.json) {
        return createEmptyStructuredJsonSample();
    }

    return {
        ...createEmptyStructuredJsonSample(),
        ...sample,
        paths: Array.isArray(sample.paths) ? sample.paths : collectJsonPaths(JSON.parse(sample.json))
    };
};

export const toPersistedStructuredJsonSample = (sample) => ({
    fileName: sample.fileName || '',
    valid: !!sample.valid,
    error: sample.error || '',
    json: sample.json || ''
});

export const createBekoSpecTableBinding = (itemsPerVisualRow = 2) => ({
    kind: 'table',
    rowsPath: '$.products[0].classifications[*].features[*]',
    columns: [
        { role: 'name', fieldPath: 'name' },
        { role: 'value', fieldPath: 'featureValues[0].value' }
    ],
    group: {
        keyPath: '../name',
        labelTemplate: '{name}'
    },
    packing: {
        enabled: true,
        itemsPerVisualRow: Number(itemsPerVisualRow) || 2,
        itemColumns: ['name', 'value']
    }
});

export const createScalarBinding = (path) => ({
    kind: 'scalar',
    path: path || ''
});

export const createImageBinding = (pathCandidates) => ({
    kind: 'image',
    pathCandidates: normalizePathList(pathCandidates)
});

export const createTableBinding = (options) => ({
    kind: 'table',
    rowsPath: options.rowsPath || '',
    columns: Array.isArray(options.columns) ? options.columns : [],
    group: {
        keyPath: options.groupKeyPath || '',
        labelTemplate: options.groupLabelTemplate || '{name}'
    },
    packing: {
        enabled: options.packingEnabled !== false,
        itemsPerVisualRow: Number(options.itemsPerVisualRow) || 2,
        itemColumns: normalizePathList(options.itemColumns)
    }
});

export const findImageCandidatePaths = (paths) => {
    return (paths || []).filter(path => {
        const lower = String(path).toLowerCase();
        return lower.includes('image') ||
            lower.includes('img') ||
            lower.includes('url') ||
            lower.endsWith('.webp') ||
            lower.endsWith('.png') ||
            lower.endsWith('.jpg') ||
            lower.endsWith('.jpeg');
    });
};

export const toFirstItemPath = (path) => {
    return String(path || '').replace(/\[\*\]/g, '[0]');
};

export const toSafePathOptions = (paths) => {
    return Array.from(new Set((paths || []).map(toFirstItemPath).filter(Boolean)));
};

export const normalizePathList = (value) => {
    if (Array.isArray(value)) {
        return value.map(v => String(v).trim()).filter(Boolean);
    }

    return String(value || '')
        .split('\n')
        .map(v => v.trim())
        .filter(Boolean);
};

export const syncBindingOnElement = (element, binding) => {
    if (!element) return element;
    return {
        ...element,
        binding,
        properties: {
            ...(element.properties || {}),
            binding
        }
    };
};
